/**
 * EditableCombobox — Input with dropdown suggestions that also allows free text.
 *
 * Uses a Popover + Command pattern: user can pick from suggestions or type anything.
 * The value is always a free-text string, not an FK id.
 */
import { useState, useRef, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn, viIncludes } from '@/lib/utils';
import { ChevronDown, X } from 'lucide-react';

export interface EditableComboboxProps {
    value: string;
    onChange: (value: string) => void;
    suggestions: string[];
    label?: string;
    placeholder?: string;
    className?: string;
    type?: string;
    /** If true, show red border */
    error?: boolean;
}

export function EditableCombobox({
    value,
    onChange,
    suggestions,
    label,
    placeholder,
    className,
    type = 'text',
    error,
}: EditableComboboxProps) {
    const [open, setOpen] = useState(false);
    const [inputValue, setInputValue] = useState(value || '');
    const [activeIndex, setActiveIndex] = useState<number>(-1);
    const [hasUserNavigatedList, setHasUserNavigatedList] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const listRef = useRef<HTMLDivElement>(null);

    // Reset activeIndex on filter change
    useEffect(() => {
        setActiveIndex(-1);
        setHasUserNavigatedList(false);
    }, [inputValue]);

    // Auto-scroll to active index
    useEffect(() => {
        if (open && listRef.current && activeIndex >= 0) {
            const activeEl = listRef.current.children[activeIndex] as HTMLElement;
            if (activeEl && activeEl.scrollIntoView) {
                activeEl.scrollIntoView({ block: 'nearest' });
            }
        }
    }, [activeIndex, open]);

    // Sync external value
    useEffect(() => {
        setInputValue(value || '');
    }, [value]);

    // Close on outside click
    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const sortedSuggestions = [...suggestions].sort((a, b) => a.localeCompare(b, 'vi', { sensitivity: 'base' }));
    const isExactMatch = suggestions.some(s => s.toLowerCase() === inputValue.trim().toLowerCase());
    const filtered = isExactMatch 
        ? sortedSuggestions 
        : sortedSuggestions.filter(s => !inputValue || viIncludes(s, inputValue));

    const handleSelect = (suggestion: string) => {
        setInputValue(suggestion);
        onChange(suggestion);
        setOpen(false);
        inputRef.current?.blur();
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const v = e.target.value;
        setInputValue(v);
        onChange(v);
        if (!open) setOpen(true);
    };

    const handleClear = () => {
        setInputValue('');
        onChange('');
        inputRef.current?.focus();
    };

    const handleBlur = () => {
        // Auto-correct to existing suggestion if matches case-insensitively
        const trimmed = inputValue.trim();
        const match = suggestions.find(s => s.toLowerCase() === trimmed.toLowerCase());
        if (match && match !== inputValue) {
            setInputValue(match);
            onChange(match);
        } else if (inputValue !== trimmed) {
            setInputValue(trimmed);
            onChange(trimmed);
        }
    };

    return (
        <div className={cn('space-y-1', className)} ref={containerRef}>
            {label && <Label className="text-xs">{label}</Label>}
            <div className="relative">
                <Input
                    ref={inputRef}
                    type={type}
                    value={inputValue}
                    onChange={handleInputChange}
                    onFocus={() => setOpen(true)}
                    onBlur={handleBlur}
                    placeholder={placeholder}
                    className={cn(
                        'pr-14 text-sm',
                        error && 'border-destructive',
                    )}
                    onKeyDown={e => {
                        // Bỏ qua nếu đang gõ tiếng Việt bằng IME
                        if (e.nativeEvent.isComposing) return;

                        if (e.key === 'ArrowDown') {
                            e.preventDefault();
                            if (!open) setOpen(true);
                            setHasUserNavigatedList(true);
                            setActiveIndex(prev => Math.min(prev + 1, filtered.length - 1));
                        } else if (e.key === 'ArrowUp') {
                            e.preventDefault();
                            setHasUserNavigatedList(true);
                            setActiveIndex(prev => Math.max(prev - 1, 0));
                        } else if (e.key === 'Enter') {
                            e.preventDefault();
                            if (open && hasUserNavigatedList && filtered.length > 0 && activeIndex >= 0 && activeIndex < filtered.length) {
                                handleSelect(filtered[activeIndex]);
                            } else {
                                // Nếu chưa dùng mũi tên chọn gợi ý, nhận chính xác giá trị người dùng vừa gõ
                                handleSelect(inputValue.trim());
                            }
                        }
                    }}
                />
                <div className="absolute right-1 top-1/2 -translate-y-1/2 flex items-center gap-0.5">
                    {inputValue && (
                        <button
                            type="button"
                            onClick={handleClear}
                            className="p-0.5 rounded hover:bg-slate-100 text-slate-400 hover:text-slate-600"
                        >
                            <X className="h-3.5 w-3.5" />
                        </button>
                    )}
                    <button
                        type="button"
                        onClick={() => { setOpen(!open); if (!open) inputRef.current?.focus(); }}
                        className="p-0.5 rounded hover:bg-slate-100 text-slate-400"
                    >
                        <ChevronDown className={cn('h-3.5 w-3.5 transition-transform', open && 'rotate-180')} />
                    </button>
                </div>

                {/* Dropdown */}
                {open && (
                    <div className="absolute z-50 mt-1 w-full rounded-md border bg-white shadow-lg max-h-48 overflow-y-auto animate-in fade-in-0 zoom-in-95 duration-100" ref={listRef}>
                        {filtered.length > 0 && (
                            filtered.map((s, index) => (
                                <button
                                    key={s}
                                    type="button"
                                    onMouseEnter={() => {
                                        setActiveIndex(index);
                                        setHasUserNavigatedList(true);
                                    }}
                                    className={cn(
                                        'w-full text-left px-3 py-1.5 text-sm transition-colors',
                                        index === activeIndex ? 'bg-slate-100' : 'hover:bg-slate-50',
                                        s === inputValue && 'font-medium text-slate-900',
                                    )}
                                    onClick={() => handleSelect(s)}
                                >
                                    {s}
                                </button>
                            ))
                        )}
                        {inputValue.trim() && !isExactMatch && (
                            <button
                                type="button"
                                className="w-full text-left px-3 py-2 text-sm text-indigo-700 bg-indigo-50/70 hover:bg-indigo-100/80 border-t border-indigo-100 transition-colors flex items-center gap-1.5 font-medium"
                                onClick={() => handleSelect(inputValue.trim())}
                            >
                                <span className="text-[11px] bg-indigo-600 text-white rounded px-1.5 py-0.5 font-bold">+ Dùng nhanh:</span>
                                <span className="truncate">{inputValue.trim()}</span>
                            </button>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
